import { useEffect, useMemo, useState } from "react";
import {
  atualizarUsuarioAcesso,
  buscarUsuarioAcesso,
  criarUsuarioAcesso,
  listarAuditoriaAcessos,
  listarCatalogoServicosAcesso,
  listarUsuariosAcesso,
  salvarVinculoUsuarioServico
} from "../api/acessosApi";
import { PERMISSOES_SERVICO } from "../auth";
import {
  emptyLink,
  emptyProfile,
  toLinkForm,
  toProfileForm
} from "./accessManagementForm";
import { GovBrButton } from "./GovBrControls";

const permissionLabels = {
  VISUALIZAR: "Visualizar",
  EDITAR: "Editar",
  PUBLICAR: "Publicar",
  DESPUBLICAR: "Retirar da publicação",
  RESTAURAR_REVISAO: "Restaurar histórico",
  ARQUIVAR: "Arquivar"
};

function errorMessage(exception, fallback) {
  return exception?.response?.data?.message || exception?.message || fallback;
}

function PermissionChecklist({ value, onChange, disabled = false }) {
  const selected = new Set(value || []);
  const toggle = (permission) => {
    const next = new Set(selected);
    if (next.has(permission)) {
      next.delete(permission);
    } else {
      next.add(permission);
    }
    onChange([...next]);
  };

  return (
    <div className="access-permissions">
      {PERMISSOES_SERVICO.map((permission) => (
        <label key={permission}>
          <input
            type="checkbox"
            checked={selected.has(permission)}
            disabled={disabled || (permission === "VISUALIZAR" && selected.size > 1)}
            onChange={() => toggle(permission)}
          />
          {permissionLabels[permission]}
        </label>
      ))}
    </div>
  );
}

export default function AccessManagement({ onBack, onPortalBack }) {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [audit, setAudit] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [profileForm, setProfileForm] = useState(emptyProfile());
  const [linkForm, setLinkForm] = useState(emptyLink());
  const [activeView, setActiveView] = useState("usuarios");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const selectedService = useMemo(
    () => catalog.find((service) => String(service.id) === String(linkForm.servicoId)),
    [catalog, linkForm.servicoId]
  );

  const loadUsers = async (filter = query) => {
    setUsers(await listarUsuariosAcesso(filter));
  };

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [userData, catalogData, auditData] = await Promise.all([
        listarUsuariosAcesso(),
        listarCatalogoServicosAcesso(),
        listarAuditoriaAcessos()
      ]);
      setUsers(userData);
      setCatalog(catalogData);
      setAudit(auditData);
    } catch (exception) {
      setError(errorMessage(exception, "Erro ao carregar a gestão de acessos."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const selectUser = async (id) => {
    setError("");
    setMessage("");
    try {
      const data = await buscarUsuarioAcesso(id);
      setSelected(data);
      setProfileForm(toProfileForm(data));
      setLinkForm(emptyLink());
    } catch (exception) {
      setError(errorMessage(exception, "Erro ao carregar o perfil."));
    }
  };

  const newUser = () => {
    setSelected(null);
    setProfileForm(emptyProfile());
    setLinkForm(emptyLink());
    setError("");
    setMessage("");
  };

  const updateProfile = (field, value) => {
    setProfileForm((current) => ({ ...current, [field]: value }));
  };

  const saveProfile = async () => {
    if (profileForm.justificativa.trim().length < 5) {
      setError("Informe uma justificativa com pelo menos 5 caracteres.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        matricula: profileForm.matricula,
        nomeExibicao: profileForm.nomeExibicao,
        ativo: profileForm.ativo,
        permiteCriarServico: profileForm.permiteCriarServico,
        permissoesNovosServicos: profileForm.permissoesNovosServicos,
        versaoEsperada: profileForm.versao,
        chamado: profileForm.chamado,
        justificativa: profileForm.justificativa
      };
      const saved = profileForm.id
        ? await atualizarUsuarioAcesso(profileForm.id, payload)
        : await criarUsuarioAcesso(payload);
      setSelected(saved);
      setProfileForm(toProfileForm(saved));
      await loadUsers();
      setAudit(await listarAuditoriaAcessos());
      setMessage(profileForm.id ? "Perfil atualizado." : "Perfil criado. O acesso será reconhecido pela matrícula no próximo login.");
    } catch (exception) {
      setError(errorMessage(exception, "Erro ao salvar o perfil."));
    } finally {
      setSaving(false);
    }
  };

  const editLink = (link) => {
    setLinkForm(toLinkForm(link));
    setError("");
    setMessage("");
  };

  const saveLink = async (override = null) => {
    const current = override || linkForm;
    if (!selected?.id) {
      setError("Selecione e salve um usuário antes de vincular serviços.");
      return;
    }
    if (!current.servicoId) {
      setError("Selecione um serviço.");
      return;
    }
    if ((current.justificativa || "").trim().length < 5) {
      setError("Informe uma justificativa com pelo menos 5 caracteres para o vínculo.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const saved = await salvarVinculoUsuarioServico(selected.id, {
        servicoId: Number(current.servicoId),
        permissoes: current.permissoes,
        ativo: current.ativo,
        expiraEm: current.expiraEm || null,
        versaoEsperada: current.versao,
        chamado: current.chamado,
        justificativa: current.justificativa
      });
      setSelected(saved);
      setProfileForm(toProfileForm(saved));
      const savedLink = (saved.servicos || []).find(
        (link) => String(link.servicoId) === String(current.servicoId)
      );
      setLinkForm(savedLink ? toLinkForm(savedLink) : emptyLink());
      await loadUsers();
      setAudit(await listarAuditoriaAcessos());
      setMessage(current.ativo ? "Permissões do serviço salvas." : "Vínculo revogado sem excluir o histórico.");
    } catch (exception) {
      setError(errorMessage(exception, "Erro ao salvar o vínculo."));
    } finally {
      setSaving(false);
    }
  };

  const revokeLink = async (link) => {
    if (!window.confirm(`Revogar o acesso de ${selected.matricula} ao serviço ${link.servicoTitulo}?`)) {
      return;
    }
    const justification = window.prompt("Justificativa da revogação (obrigatória):", "Acesso revogado por solicitação da área.");
    if (justification == null) {
      return;
    }
    const ticket = window.prompt("Número do chamado/solicitação da revogação (opcional):", "");
    if (ticket == null) {
      return;
    }
    await saveLink({
      ...toLinkForm(link),
      ativo: false,
      chamado: ticket,
      justificativa: justification
    });
  };

  const runSearch = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await loadUsers(query);
    } catch (exception) {
      setError(errorMessage(exception, "Erro ao pesquisar usuários."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="portal-services-shell portal-admin-shell" id="portal-acessos-admin">
      <div className="admin-topbar">
        <div>
          <span className="service-category">Administração de acessos</span>
          <h1>Perfis e serviços por matrícula</h1>
          <p>O Keycloak valida o login, e a aplicação localiza as permissões pela matrícula.</p>
        </div>
        <div className="admin-actions">
          <GovBrButton onClick={onBack}>Cadastro de serviços</GovBrButton>
          <GovBrButton emphasis="tertiary" onClick={onPortalBack}>Portal público</GovBrButton>
        </div>
      </div>

      {error && <div className="admin-message"><br-message message={error} show-icon="true" state="danger"></br-message></div>}
      {message && <div className="admin-message"><br-message message={message} show-icon="true" state="success"></br-message></div>}

      <nav className="access-view-tabs" aria-label="Áreas da gestão de acessos">
        <button className={activeView === "usuarios" ? "is-active" : ""} type="button" onClick={() => setActiveView("usuarios")}>Usuários e vínculos</button>
        <button className={activeView === "auditoria" ? "is-active" : ""} type="button" onClick={() => setActiveView("auditoria")}>Auditoria ({audit.length})</button>
      </nav>

      {activeView === "auditoria" ? (
        <section className="admin-form-panel access-audit-panel">
          <div className="admin-form-heading">
            <div><h2>Últimas alterações de acesso</h2><p>São exibidos os 200 eventos mais recentes.</p></div>
            <GovBrButton onClick={async () => setAudit(await listarAuditoriaAcessos())}>Atualizar</GovBrButton>
          </div>
          <div className="revision-list access-audit-list">
            {audit.map((event) => (
              <article className="revision-row revision-row--audit" key={event.id}>
                <div>
                  <strong>{event.acao} · {event.alvoMatricula || "-"}</strong>
                  <span>Serviço: {event.servicoId || "geral"} · Chamado: {event.chamado || "não informado"}</span>
                  <small>{event.atorMatricula} · {event.dataHora} · {event.justificativa}</small>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : (
        <div className="admin-layout access-layout">
          <aside className="admin-list-panel">
            <div className="admin-list-header"><h2>Usuários</h2><GovBrButton onClick={newUser}>Novo</GovBrButton></div>
            <form className="access-search" onSubmit={runSearch}>
              <input aria-label="Matrícula ou nome" placeholder="Matrícula ou nome" value={query} onChange={(event) => setQuery(event.target.value)} />
              <GovBrButton type="submit">Buscar</GovBrButton>
            </form>
            {loading ? <p className="access-padding">Carregando...</p> : (
              <div className="admin-service-list">
                {users.map((user) => (
                  <button className={`admin-service-row${selected?.id === user.id ? " is-selected" : ""}`} type="button" key={user.id} onClick={() => selectUser(user.id)}>
                    <strong>{user.matricula} · {user.nomeExibicao}</strong>
                    <span>{user.ativo ? "ativo" : "inativo"} · {user.quantidadeServicos} serviço(s)</span>
                  </button>
                ))}
              </div>
            )}
          </aside>

          <section className="admin-form-panel">
            <div className="admin-form-heading">
              <div><h2>{profileForm.id ? "Editar perfil" : "Novo perfil"}</h2><p>Informe o chamado e a justificativa de cada alteração.</p></div>
              <div className="admin-actions">
                <GovBrButton emphasis="primary" disabled={saving} loading={saving} onClick={saveProfile}>Salvar perfil</GovBrButton>
              </div>
            </div>
            <div className="admin-form-grid access-form-grid">
              <label className="admin-field"><span>Matrícula</span><input value={profileForm.matricula} onChange={(event) => updateProfile("matricula", event.target.value)} /></label>
              <label className="admin-field"><span>Nome de exibição</span><input value={profileForm.nomeExibicao} onChange={(event) => updateProfile("nomeExibicao", event.target.value)} /></label>
              <label className="access-check"><input type="checkbox" checked={profileForm.ativo} onChange={(event) => updateProfile("ativo", event.target.checked)} /> Perfil ativo</label>
              <label className="access-check"><input type="checkbox" checked={profileForm.permiteCriarServico} onChange={(event) => updateProfile("permiteCriarServico", event.target.checked)} /> Pode criar serviços</label>
              <div className="field--full"><strong>Permissões aplicadas aos serviços que este usuário criar</strong><PermissionChecklist value={profileForm.permissoesNovosServicos} onChange={(value) => updateProfile("permissoesNovosServicos", value)} /></div>
              <label className="admin-field"><span>Chamado/solicitação</span><input value={profileForm.chamado} onChange={(event) => updateProfile("chamado", event.target.value)} /></label>
              <label className="admin-field"><span>Justificativa</span><textarea rows="3" value={profileForm.justificativa} onChange={(event) => updateProfile("justificativa", event.target.value)} /></label>
            </div>

            {selected && (
              <section className="access-links-section">
                <div className="admin-form-heading">
                  <div><h2>Serviços vinculados</h2><p>Revogar apenas desativa o vínculo; nenhum histórico é apagado.</p></div>
                  <GovBrButton onClick={() => setLinkForm(emptyLink())}>Novo vínculo</GovBrButton>
                </div>
                <div className="revision-list access-current-links">
                  {(selected.servicos || []).map((link) => (
                    <article className="revision-row" key={link.id}>
                      <div><strong>{link.servicoTitulo}</strong><span>{(link.permissoes || []).map((item) => permissionLabels[item]).join(", ") || "Sem permissões"}</span><small>{link.ativo ? "Ativo" : "Revogado"}{link.expiraEm ? ` · expira em ${link.expiraEm}` : ""}</small><small>Última solicitação: {link.chamado || "não informada"} · {link.justificativa || "sem justificativa registrada"}</small></div>
                      <div className="admin-actions"><GovBrButton onClick={() => editLink(link)}>Editar</GovBrButton>{link.ativo && <GovBrButton onClick={() => revokeLink(link)}>Revogar</GovBrButton>}</div>
                    </article>
                  ))}
                </div>
                <div className="admin-form-grid access-form-grid access-link-form">
                  <label className="admin-field field--full"><span>Serviço</span><select value={linkForm.servicoId} disabled={linkForm.versao != null} onChange={(event) => setLinkForm((current) => ({ ...current, servicoId: event.target.value, versao: null }))}><option value="">Selecione</option>{catalog.map((service) => <option key={service.id} value={service.id}>{service.titulo} · {service.status}</option>)}</select></label>
                  <div className="field--full"><strong>Permissões em {selectedService?.titulo || "serviço selecionado"}</strong><PermissionChecklist value={linkForm.permissoes} onChange={(value) => setLinkForm((current) => ({ ...current, permissoes: value }))} /></div>
                  <label className="access-check"><input type="checkbox" checked={linkForm.ativo} onChange={(event) => setLinkForm((current) => ({ ...current, ativo: event.target.checked }))} /> Vínculo ativo</label>
                  <label className="admin-field"><span>Expira em (opcional)</span><input type="datetime-local" value={linkForm.expiraEm} onChange={(event) => setLinkForm((current) => ({ ...current, expiraEm: event.target.value }))} /></label>
                  <label className="admin-field"><span>Chamado/solicitação</span><input value={linkForm.chamado} onChange={(event) => setLinkForm((current) => ({ ...current, chamado: event.target.value }))} /></label>
                  <label className="admin-field"><span>Justificativa</span><textarea rows="3" value={linkForm.justificativa} onChange={(event) => setLinkForm((current) => ({ ...current, justificativa: event.target.value }))} /></label>
                  <div className="field--full"><GovBrButton emphasis="primary" disabled={saving} onClick={() => saveLink()}>Salvar vínculo</GovBrButton></div>
                </div>
              </section>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
